import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAssessment } from "@/context/AssessmentContext";
import {
  QUESTIONS_BY_ROLE,
  AREAS_BY_ROLE,
  OBIETTIVI_BY_ROLE,
  SCALE_LABELS,
} from "@/data/questions";
import { submitAssessment } from "@/lib/api";
import {
  trackQuestionAdvance,
  trackAssessmentAbandoned,
  trackAssessmentCompleted,
} from "@/lib/analytics";
import BDFLogo from "@/components/BDFLogo";

export default function Assessment() {
  const navigate = useNavigate();
  const { userInfo, answers, setAnswers, obiettivo, setObiettivo, setResult } = useAssessment();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [direction, setDirection] = useState(1);
  const completedRef = useRef(false);
  const stepRef = useRef(0);
  const ruoloRef = useRef(null);

  useEffect(() => {
    if (!userInfo) {
      navigate("/info", { replace: true });
    }
  }, [userInfo, navigate]);

  const ruolo = userInfo?.ruolo || "danzatore";
  const questions = QUESTIONS_BY_ROLE[ruolo] || [];
  const areas = AREAS_BY_ROLE[ruolo] || [];
  const obiettivi = OBIETTIVI_BY_ROLE[ruolo] || [];
  const TOTAL_STEPS = questions.length + 1;

  const isGoalStep = step === questions.length;
  const currentQuestion = !isGoalStep ? questions[step] : null;
  const currentArea = useMemo(() => {
    if (isGoalStep || !currentQuestion) return null;
    return areas.find((a) => a.id === currentQuestion.area);
  }, [isGoalStep, currentQuestion, areas]);

  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  // Keep refs in sync for the abandon handler (runs on unmount/unload)
  useEffect(() => {
    stepRef.current = step;
    ruoloRef.current = ruolo;
    // Scroll to top on every question change so the sticky header never hides the
    // beginning of the new question text (fixes mobile "cropped question" issue).
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [step, ruolo]);

  // Track assessment abandoned on page unload if not completed
  useEffect(() => {
    const handler = () => {
      if (completedRef.current) return;
      if (stepRef.current === 0 && !answers) return;
      const qs = QUESTIONS_BY_ROLE[ruoloRef.current] || [];
      const idx = Math.min(stepRef.current, qs.length - 1);
      const q = qs[idx];
      if (!q) return;
      trackAssessmentAbandoned({
        last_question_number: idx + 1,
        last_question_id: q.id,
        ruolo: ruoloRef.current,
      });
    };
    window.addEventListener("pagehide", handler);
    return () => {
      // Also fire on component unmount (e.g. user navigates away without completing)
      window.removeEventListener("pagehide", handler);
      if (!completedRef.current && stepRef.current > 0) {
        handler();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (val) => {
    setAnswers({ ...answers, [String(currentQuestion.id)]: val });
    // GA4: question_advance
    trackQuestionAdvance({
      question_number: step + 1,
      question_id: currentQuestion.id,
      area: currentQuestion.area,
      ruolo,
      value: val,
    });
    setTimeout(() => {
      if (step < questions.length) {
        setDirection(1);
        setStep((s) => s + 1);
      }
    }, 220);
  };

  const handleNext = () => {
    if (isGoalStep) {
      if (!obiettivo) {
        toast.error("Seleziona il tuo obiettivo principale");
        return;
      }
      handleSubmit();
      return;
    }
    if (!answers[String(currentQuestion.id)]) {
      toast.error("Seleziona una risposta");
      return;
    }
    setDirection(1);
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step === 0) {
      navigate("/info");
      return;
    }
    setDirection(-1);
    setStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      for (const q of questions) {
        if (!answers[String(q.id)]) {
          const idx = questions.findIndex((qq) => qq.id === q.id);
          toast.error(`Risposta mancante alla domanda ${idx + 1}`);
          setSubmitting(false);
          setStep(idx);
          return;
        }
      }
      const cleanedAnswers = Object.fromEntries(
        questions.map((q) => [String(q.id), Number(answers[String(q.id)])])
      );
      const data = await submitAssessment({
        user_info: userInfo,
        answers: cleanedAnswers,
        obiettivo,
      });
      setResult(data);
      completedRef.current = true;
      // GA4: assessment_completed
      trackAssessmentCompleted({
        profilo: data?.profilo,
        ruolo,
        total_questions: questions.length,
        totale: data?.areas?.totale,
      });
      navigate("/risultati");
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Errore durante l'invio. Riprova.";
      toast.error(typeof msg === "string" ? msg : "Errore durante l'invio");
    } finally {
      setSubmitting(false);
    }
  };

  if (!userInfo || questions.length === 0) return null;

  const currentValue = currentQuestion ? answers[String(currentQuestion.id)] : null;
  const totalQuestions = questions.length;

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      <header className="px-6 sm:px-10 lg:px-16 pt-8 pb-6 sticky top-0 z-30 bg-[#F9F8F6]/85 backdrop-blur-md">
        <div className="max-w-[1100px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <BDFLogo />
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#5C7368]" data-testid="assessment-step-counter">
              {step + 1} / {TOTAL_STEPS}
            </span>
          </div>
          <div className="relative h-[2px] w-full bg-[#DCE3DF] rounded-full overflow-hidden" data-testid="progress-bar-track">
            <motion.div
              className="absolute left-0 top-0 h-full bg-[#1E3F33]"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              data-testid="progress-bar-fill"
            />
          </div>
          {currentArea && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#1E3F33] font-medium" data-testid="assessment-area-title">
                {currentArea.title}
              </span>
              <span className="text-[11px] uppercase tracking-[0.18em] text-[#5C7368]">
                {currentArea.subtitle}
              </span>
            </div>
          )}
          {isGoalStep && (
            <div className="mt-4">
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#1E3F33] font-medium" data-testid="assessment-area-title">
                Il tuo obiettivo
              </span>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 px-6 sm:px-10 lg:px-16 py-10 sm:py-16">
        <div className="max-w-[860px] mx-auto">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="w-full"
            >
              {!isGoalStep && (
                <QuestionView
                  number={step + 1}
                  total={totalQuestions + 1}
                  question={currentQuestion}
                  value={currentValue}
                  onSelect={handleSelect}
                />
              )}
              {isGoalStep && (
                <GoalView value={obiettivo} onChange={setObiettivo} options={obiettivi} number={totalQuestions + 1} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <footer className="px-6 sm:px-10 lg:px-16 py-8 border-t border-[#DCE3DF]/60 bg-white/40">
        <div className="max-w-[860px] mx-auto flex items-center justify-between gap-4">
          <Button
            variant="ghost"
            onClick={handleBack}
            data-testid="assessment-back-button"
            className="rounded-full text-[#5C7368] hover:text-[#1E3F33] hover:bg-[#EFECE7] text-xs tracking-[0.16em] uppercase"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            {step === 0 ? "Esci" : "Indietro"}
          </Button>
          <Button
            onClick={handleNext}
            disabled={submitting}
            data-testid="assessment-next-button"
            className="rounded-full h-12 px-7 bg-[#1E3F33] hover:bg-[#2A5243] text-white text-xs tracking-[0.16em] uppercase font-semibold transition-all disabled:opacity-50 group"
          >
            {isGoalStep ? (submitting ? "Calcolo in corso..." : "Scopri il tuo profilo") : "Avanti"}
            <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </footer>
    </div>
  );
}

const QuestionView = ({ number, total, question, value, onSelect }) => (
  <div className="w-full">
    <div className="text-xs uppercase tracking-[0.2em] text-[#5C7368] mb-4" data-testid="question-number">
      Domanda {number} di {total}
    </div>
    <h2
      className="font-display text-2xl sm:text-4xl lg:text-5xl leading-[1.2] sm:leading-[1.15] text-[#1E3F33] tracking-tight break-words hyphens-auto w-full"
      data-testid="question-text"
    >
      {question.text}
    </h2>
    <p className="mt-4 text-sm text-[#5C7368] break-words" data-testid="question-helper">
      Scegli su una scala da 1 (Mai) a 5 (Sempre).
    </p>

    <div className="mt-12 grid grid-cols-5 gap-2 sm:gap-3">
      {SCALE_LABELS.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onSelect(opt.value)}
            data-testid={`scale-option-${opt.value}`}
            className={`likert-option group relative aspect-[3/4] sm:aspect-[5/6] rounded-2xl border flex flex-col items-center justify-between p-3 sm:p-4 text-center
              ${selected
                ? "bg-[#1E3F33] border-[#1E3F33] text-white shadow-lg"
                : "bg-white border-[#DCE3DF] hover:border-[#1E3F33]/50 text-[#1E3F33]"
              }`}
          >
            <div className="flex items-center justify-center w-full pt-2">
              <span className={`font-display text-3xl sm:text-4xl lg:text-5xl ${selected ? "text-white" : "text-[#1E3F33]"}`}>
                {opt.value}
              </span>
            </div>
            <div className={`text-[10px] sm:text-xs uppercase tracking-[0.14em] font-medium ${selected ? "text-white/90" : "text-[#5C7368]"}`}>
              {opt.label}
            </div>
            {selected && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white/95 flex items-center justify-center">
                <Check className="w-3 h-3 text-[#1E3F33]" strokeWidth={3} />
              </div>
            )}
          </button>
        );
      })}
    </div>
  </div>
);

const GoalView = ({ value, onChange, options, number }) => (
  <div className="w-full">
    <div className="text-xs uppercase tracking-[0.2em] text-[#5C7368] mb-4" data-testid="question-number">
      Domanda {number} di {number}
    </div>
    <h2
      className="font-display text-2xl sm:text-4xl lg:text-5xl leading-[1.2] sm:leading-[1.15] text-[#1E3F33] tracking-tight break-words hyphens-auto w-full"
      data-testid="question-text"
    >
      Qual è oggi il tuo obiettivo principale?
    </h2>
    <p className="mt-4 text-sm text-[#5C7368]">
      Scegli ciò che ti rappresenta in questo momento.
    </p>
    <div className="mt-10 grid sm:grid-cols-2 gap-3" data-testid="goal-options">
      {options.map((g) => {
        const selected = value === g;
        return (
          <button
            key={g}
            onClick={() => onChange(g)}
            data-testid={`goal-option-${g.replace(/\s+/g, "-").toLowerCase()}`}
            className={`text-left p-5 rounded-2xl border transition-all ${
              selected
                ? "bg-[#1E3F33] border-[#1E3F33] text-white shadow-lg"
                : "bg-white border-[#DCE3DF] hover:border-[#1E3F33]/40 text-[#1E3F33]"
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`mt-0.5 w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${
                  selected ? "border-white bg-white" : "border-[#5C7368]"
                }`}
              >
                {selected && <span className="w-2 h-2 rounded-full bg-[#1E3F33]" />}
              </div>
              <span className={`font-medium leading-snug ${selected ? "text-white" : "text-[#1E3F33]"}`}>
                {g}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);
